import { supabase } from './supabaseClient';
import { DEFAULT_PLANT_TIER_ID, PLANT_TIERS } from '../utils/Chloromance/constants';
import type { ChloromanceTierId, PlantCatalogueType } from '../utils/Chloromance/types';

export type CloudPlant = {
  id: string;
  name: string;
  variety: string;
  plant_type?: string;
  plant_count?: number;
  chloromance_zone: string;
  watering_frequency_days: number;
  last_watered_timestamp: number;
  note?: string;
  catalogue_id?: string;
  catalogue_type?: PlantCatalogueType;
  suggested_location?: string;
  fruit_or_flower_months?: number[];
  best_harvest_months?: number[];
};

type UserPlantRow = {
  id: string;
  user_id: string;
  name: string;
  variety: string;
  plant_type: string | null;
  plant_count: number | null;
  chloromance_zone: string;
  watering_frequency_days: number;
  last_watered_timestamp: number;
  note: string | null;
  catalogue_id: string | null;
  catalogue_type: PlantCatalogueType | null;
  suggested_location: string | null;
  fruit_or_flower_months: number[] | null;
  best_harvest_months: number[] | null;
};

type UserTierRow = {
  chloromance_tier_id: string | null;
};

const BASE_PLANT_SELECT =
  'id,user_id,name,variety,plant_type,plant_count,chloromance_zone,watering_frequency_days,last_watered_timestamp,note';
const EXTENDED_PLANT_SELECT =
  `${BASE_PLANT_SELECT},catalogue_id,catalogue_type,suggested_location,fruit_or_flower_months,best_harvest_months`;

function isChloromanceTierId(value: string | null | undefined): value is ChloromanceTierId {
  return PLANT_TIERS.some((tier) => tier.id === value);
}

function getSupabaseErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object') {
    const candidate = error as { message?: unknown; details?: unknown; hint?: unknown };
    if (typeof candidate.message === 'string') return candidate.message;
    if (typeof candidate.details === 'string') return candidate.details;
    if (typeof candidate.hint === 'string') return candidate.hint;
  }
  return fallback;
}

function fromRow(row: UserPlantRow): CloudPlant {
  return {
    id: row.id,
    name: row.name,
    variety: row.variety,
    plant_type: row.plant_type || undefined,
    plant_count: Number.isFinite(row.plant_count) ? Math.max(1, Math.round(row.plant_count || 1)) : 1,
    chloromance_zone: row.chloromance_zone,
    watering_frequency_days: row.watering_frequency_days,
    last_watered_timestamp: row.last_watered_timestamp,
    note: row.note || undefined,
    catalogue_id: row.catalogue_id || undefined,
    catalogue_type: row.catalogue_type || undefined,
    suggested_location: row.suggested_location || undefined,
    fruit_or_flower_months: row.fruit_or_flower_months || undefined,
    best_harvest_months: row.best_harvest_months || undefined,
  };
}

function toBaseRow(userId: string, plant: CloudPlant) {
  return {
    id: plant.id,
    user_id: userId,
    name: plant.name,
    variety: plant.variety,
    plant_type: plant.plant_type || null,
    plant_count: Number.isFinite(plant.plant_count) ? Math.max(1, Math.round(plant.plant_count || 1)) : 1,
    chloromance_zone: plant.chloromance_zone,
    watering_frequency_days: plant.watering_frequency_days,
    last_watered_timestamp: plant.last_watered_timestamp,
    note: plant.note || null,
    updated_at: new Date().toISOString(),
  };
}

function toRow(userId: string, plant: CloudPlant) {
  return {
    ...toBaseRow(userId, plant),
    catalogue_id: plant.catalogue_id || null,
    catalogue_type: plant.catalogue_type || null,
    suggested_location: plant.suggested_location || null,
    fruit_or_flower_months: plant.fruit_or_flower_months || null,
    best_harvest_months: plant.best_harvest_months || null,
  };
}

export async function loadUserPlants(userId: string): Promise<CloudPlant[]> {
  const query = supabase
    .from('user_plants')
    .select(EXTENDED_PLANT_SELECT)
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });
  const { data, error } = await query;

  if (!error) {
    return (data || []).map((row) => fromRow(row as UserPlantRow));
  }

  const fallback = await supabase
    .from('user_plants')
    .select(BASE_PLANT_SELECT)
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (fallback.error) {
    throw fallback.error;
  }

  return (fallback.data || []).map((row) => fromRow(row as UserPlantRow));
}

export async function saveUserPlants(userId: string, plants: CloudPlant[]) {
  if (plants.length === 0) {
    return;
  }

  const { error } = await supabase.from('user_plants').upsert(plants.map((plant) => toRow(userId, plant)));

  if (!error) {
    return;
  }

  const fallback = await supabase.from('user_plants').upsert(plants.map((plant) => toBaseRow(userId, plant)));

  if (fallback.error) {
    throw fallback.error;
  }
}

export async function saveUserPlant(userId: string, plant: CloudPlant) {
  await saveUserPlants(userId, [plant]);
}

export async function deleteUserPlant(userId: string, plantId: string) {
  const { error } = await supabase.from('user_plants').delete().eq('user_id', userId).eq('id', plantId);

  if (error) {
    throw error;
  }
}

export async function loadUserTier(userId: string): Promise<ChloromanceTierId> {
  const { data, error } = await supabase
    .from('user_subscriptions')
    .select('chloromance_tier_id')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  const tierId = (data as UserTierRow | null)?.chloromance_tier_id;
  return isChloromanceTierId(tierId) ? tierId : DEFAULT_PLANT_TIER_ID;
}

export async function saveUserTier(userId: string, tierId: ChloromanceTierId) {
  const { error } = await supabase
    .from('user_subscriptions')
    .upsert({ user_id: userId, chloromance_tier_id: tierId, updated_at: new Date().toISOString() });

  if (!error) {
    return;
  }

  const fallback = await supabase.from('user_subscriptions').upsert({ user_id: userId, chloromance_tier_id: tierId });

  if (fallback.error) {
    throw fallback.error;
  }
}

export async function redeemUpgradeCoupon(couponCode: string): Promise<ChloromanceTierId> {
  const normalizedCode = couponCode.trim().toUpperCase();
  if (!normalizedCode) {
    throw new Error('Enter a coupon code.');
  }

  const { data, error } = await supabase.rpc('redeem_chloromance_coupon', { coupon_code: normalizedCode });

  if (error) {
    throw new Error(getSupabaseErrorMessage(error, 'Coupon could not be redeemed.'));
  }

  if (!isChloromanceTierId(data as string | null | undefined)) {
    throw new Error('Coupon redeemed, but the tier was not recognized.');
  }

  return data as ChloromanceTierId;
}
